/**
 * Transaction GraphQL Fragments
 *
 * Reusable fragments for Transaction primitive.
 * Aligned with transaction_model.rs GqlTransaction exposed fields.
 *
 * Fields marked #[graphql(skip)] in model are NOT included:
 * - tenantId, checkpointIds, compensationTaskIds, eventIds
 * - workflowInstanceId, parentTransactionId, notificationIds
 * - lastCheckpointId, complianceOfficerId (use resolver)
 * - participantTenantIds, settlementTenantId
 * - createdBy, updatedBy (use resolvers)
 */

import { gql } from '@apollo/client';

/**
 * Nested object fragments
 */
export const RETRY_POLICY_FIELDS = gql`
  fragment RetryPolicyFields on RetryPolicy {
    maxAttempts
    backoffMs
    exponential
    maxBackoffMs
  }
`;

export const XA_PARTICIPANT_FIELDS = gql`
  fragment XAParticipantFields on XAParticipant {
    resourceManagerId
    resourceManagerUrl
    xaResourceState
    vote
    preparedAt
    committedAt
    errorMessage
  }
`;

export const SAFETY_OVERRIDE_FIELDS = gql`
  fragment SafetyOverrideFields on SafetyOverride {
    overrideTimestamp
    overriddenBy
    overriddenByRole
    safetyCheckFailed
    overrideReason
    supervisorApprovalId
    overrideIpAddress
    overrideDeviceId
  }
`;

export const WITNESS_SIGNATURE_FIELDS = gql`
  fragment WitnessSignatureFields on WitnessSignature {
    witnessId
    witnessName
    witnessRole
    signatureType
    signedAt
    signatureData
    certificateId
    notarizationJurisdiction
  }
`;

export const CROSS_TENANT_AUTH_FIELDS = gql`
  fragment CrossTenantAuthFields on CrossTenantAuth {
    tenantId
    authorizedBy
    authorizedAt
    authorizationType
    authorizationDocumentId
    expiration
    revoked
    revokedAt
  }
`;

/**
 * Complete Transaction fields fragment
 *
 * Only includes non-skipped fields from transaction_model.rs
 */
export const TRANSACTION_FIELDS = gql`
  ${RETRY_POLICY_FIELDS}
  ${XA_PARTICIPANT_FIELDS}
  ${SAFETY_OVERRIDE_FIELDS}
  ${WITNESS_SIGNATURE_FIELDS}
  ${CROSS_TENANT_AUTH_FIELDS}
  fragment TransactionFields on Transaction {
    # Core Identity (3 fields - tenantId is graphql(skip))
    transactionId
    transactionName
    transactionCode

    # Runtime State (6 fields - lastCheckpointId is graphql(skip))
    status
    startedAt
    committedAt
    rolledBackAt
    failedAt
    currentStep
    totalSteps

    # Transaction Semantics (7 fields)
    isolationLevel
    timeoutSeconds
    retryPolicy {
      ...RetryPolicyFields
    }
    idempotencyKey
    isNested
    requiresTwoPhaseCommit
    autoCommit

    # Saga Configuration (5 fields)
    compensationStrategy
    rollbackOnAnyFailure
    preservePartialState
    maxCompensationAttempts
    compensationTimeoutSeconds

    # XA Transactions (6 fields)
    xaTransactionId
    xaBranchQualifier
    xaFormatId
    xaState
    xaParticipants {
      ...XAParticipantFields
    }
    xaCoordinatorUrl

    # Life-Critical Safety (7 fields)
    isLifeCritical
    safetyValidationRequired
    emergencyAbortEnabled
    safetyChecklistIds
    safetyApprovalRequired
    safetyApproverRoles
    safetyOverrideLog {
      ...SafetyOverrideFields
    }

    # Regulatory Compliance (6 fields - complianceOfficerId is graphql(skip), use resolver)
    complianceFrameworks
    auditTrailRequired
    auditTrailRetentionYears
    cryptographicSignature
    witnessSignatures {
      ...WitnessSignatureFields
    }
    regulatoryDeadline
    regulatorySubmissionId

    # Distributed Locking (5 fields)
    distributedLockId
    lockTimeoutSeconds
    lockAcquiredAt
    lockHolderInstanceId
    deadlockDetectionEnabled

    # Cross-Tenant (2 fields - participantTenantIds, settlementTenantId are graphql(skip))
    isCrossTenant
    crossTenantAuthorization {
      ...CrossTenantAuthFields
    }

    # Lifecycle (2 fields - createdBy, updatedBy are graphql(skip), use resolvers)
    createdAt
    updatedAt

    # Performance Metrics (4 fields)
    executionTimeMs
    rollbackTimeMs
    checkpointOverheadMs
    retryCount

    # Complex Object Resolvers (4 fields)
    compressedId
    complianceOfficer {
      id
      displayName
      role
    }
    createdByActor {
      id
      displayName
      role
    }
    updatedByActor {
      id
      displayName
      role
    }
  }
`;

/**
 * Transaction statistics fields
 */
export const TRANSACTION_STATISTICS_FIELDS = gql`
  fragment TransactionStatisticsFields on TransactionStatistics {
    totalTransactions
    committedCount
    rolledBackCount
    failedCount
    avgExecutionTimeMs
    avgSteps
    successRate
  }
`;
